import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logoIcon from '../../assets/images/new_log_design.svg';

/**
 * Clean, modern Loader component designed exactly like the linear progress UI.
 * Features:
 * - Emthany SVG logo centrally featured with a much larger size and gentle floating animation.
 * - Perfectly centered vertically & horizontally in the middle of the page.
 * - Bold, crisp loading text positioned below the logo.
 * - Sleek horizontal indeterminate rounded progress bar below the text.
 */
const Loader = ({
    isLoading = true,
    text = "جاري التحميل...",
    fullScreen = false,
    mode = "container",
    size = "md",
    className = ""
}) => {
    const isFullScreenMode = fullScreen || mode === "fullscreen";

    // Significantly larger logo and progress bar sizes
    const sizeConfig = {
        sm: { logo: 100, barWidth: 220, barHeight: 6, fontSize: "1.05rem" },
        md: { logo: 145, barWidth: 280, barHeight: 7, fontSize: "1.25rem" },
        lg: { logo: 195, barWidth: 340, barHeight: 8, fontSize: "1.45rem" }
    }[size] || { logo: 145, barWidth: 280, barHeight: 7, fontSize: "1.25rem" };

    return (
        <AnimatePresence mode="wait">
            {isLoading && (
                <motion.div
                    role="status"
                    aria-live="polite"
                    aria-busy={isLoading}
                    aria-label={text || "Loading"}
                    className={`loader-root ${isFullScreenMode ? 'fullscreen-overlay' : 'container-overlay'} ${className}`}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                    <div className="loader-card">
                        {/* Central Brand SVG Logo (Significantly Larger) with Gentle Floating Animation */}
                        <motion.div
                            className="logo-wrapper"
                            animate={{
                                y: [0, -10, 0],
                                scale: [0.98, 1.03, 0.98]
                            }}
                            transition={{
                                duration: 2.6,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <img
                                src={logoIcon}
                                alt="EMt7any Logo"
                                className="brand-logo"
                                style={{ width: `${sizeConfig.logo}px`, height: `${sizeConfig.logo}px` }}
                            />
                        </motion.div>

                        {/* Loading Text */}
                        {text && (
                            <motion.p
                                className="loading-text"
                                style={{ fontSize: sizeConfig.fontSize }}
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.3 }}
                            >
                                {text}
                            </motion.p>
                        )}

                        {/* Sleek Horizontal Indeterminate Progress Bar */}
                        <div
                            className="progress-track"
                            style={{ width: `${sizeConfig.barWidth}px`, height: `${sizeConfig.barHeight}px` }}
                        >
                            <motion.div
                                className="progress-thumb"
                                animate={{
                                    x: ["-100%", "220%"]
                                }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            />
                        </div>

                        <span className="sr-only">{text || "Loading..."}</span>
                    </div>

                    <style jsx>{`
                        .loader-root {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            height: 100%;
                            box-sizing: border-box;
                            background: #ffffff;
                        }

                        .container-overlay {
                            position: relative;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            min-height: 70vh;
                            width: 100%;
                            padding: 2rem;
                            z-index: 10;
                            border-radius: 20px;
                        }

                        .fullscreen-overlay {
                            position: fixed;
                            inset: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 100vw;
                            height: 100vh;
                            z-index: 9999;
                            background: #ffffff;
                        }

                        .loader-card {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            text-align: center;
                            width: 100%;
                            margin: auto 0;
                        }

                        .logo-wrapper {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-bottom: 24px;
                            filter: drop-shadow(0 6px 16px rgba(122, 106, 216, 0.25));
                        }

                        .brand-logo {
                            object-fit: contain;
                            user-select: none;
                            pointer-events: none;
                        }

                        .loading-text {
                            margin: 0 0 16px 0;
                            color: #0f172a;
                            font-family: "Zain", sans-serif;
                            font-weight: 700;
                            letter-spacing: 0.3px;
                        }

                        .progress-track {
                            position: relative;
                            background-color: #e0e7ff;
                            border-radius: 9999px;
                            overflow: hidden;
                        }

                        .progress-thumb {
                            position: absolute;
                            top: 0;
                            left: 0;
                            height: 100%;
                            width: 45%;
                            background: linear-gradient(90deg, #4f46e5 0%, #3b82f6 100%);
                            border-radius: 9999px;
                        }

                        .sr-only {
                            position: absolute;
                            width: 1px;
                            height: 1px;
                            padding: 0;
                            margin: -1px;
                            overflow: hidden;
                            clip: rect(0, 0, 0, 0);
                            border: 0;
                        }

                        @media (max-width: 640px) {
                            .container-overlay {
                                min-height: 55vh;
                            }
                        }
                    `}</style>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Loader;